import SearchableSelect from '@/components/SearchableSelect';
import { useState, useEffect } from 'react';
import { billingOpsAPI, customersAPI, banksAPI, billingAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const PAY_MODES = [
  { code: 'cash', label: 'Cash' }, { code: 'card', label: 'Card' },
  { code: 'upi', label: 'UPI' }, { code: 'bank_transfer', label: 'Bank Transfer' },
  { code: 'cheque', label: 'Cheque' },
];

/**
 * Admin "Record Advance" — books a customer advance (not tied to an invoice)
 * as a customer credit. Reused on the Payments page and the Customer drill-down.
 */
export default function RecordAdvanceDialog({ open, onOpenChange, prefillCustomer = null, onSaved }) {
  const [form, setForm] = useState({
    customer_id: '', customer_name: '', amount: '', payment_mode: 'cash',
    deposit_to: '', clinic_id: '', clinic_name: '',
    payment_date: new Date().toISOString().split('T')[0], reference: '', notes: '',
  });
  const [custQuery, setCustQuery] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [showCustDrop, setShowCustDrop] = useState(false);
  const [banks, setBanks] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    banksAPI.list().then(({ data }) => setBanks(data.banks || [])).catch(() => {});
    billingAPI.adminGetBranches().then(({ data }) => setClinics(data.branches || [])).catch(() => {});
    if (prefillCustomer) {
      setForm(f => ({ ...f, customer_id: prefillCustomer.id || '', customer_name: prefillCustomer.name || '' }));
      setCustQuery(prefillCustomer.name || '');
    }
  }, [open, prefillCustomer]);

  useEffect(() => {
    if (prefillCustomer) return;
    const q = custQuery.trim();
    if (q.length < 2) { setCustResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await customersAPI.list({ search: q, per_page: 10 }); setCustResults(data.customers || []); }
      catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [custQuery, prefillCustomer]);

  const selectCustomer = (c) => {
    const name = c.display_name || c.contact_name || c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim();
    setForm(f => ({ ...f, customer_id: c.id || c.zoho_id || '', customer_name: name }));
    setCustQuery(name);
    setShowCustDrop(false);
    setCustResults([]);
  };

  const submit = async () => {
    const amt = parseFloat(form.amount) || 0;
    if (!form.customer_name.trim() && !form.customer_id) { toast.error('Select a customer'); return; }
    if (amt <= 0) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    try {
      const clinic = clinics.find(c => c.id === form.clinic_id);
      await billingOpsAPI.recordAdvance({
        customer_id: form.customer_id,
        customer_name: form.customer_name,
        amount: amt,
        payment_mode: form.payment_mode,
        deposit_to: form.deposit_to,
        payment_date: form.payment_date,
        reference: form.reference,
        notes: form.notes,
        clinic_id: form.clinic_id,
        clinic_name: clinic?.name || form.clinic_name || '',
      });
      toast.success(`Advance of ₹${amt.toLocaleString('en-IN')} recorded for ${form.customer_name}`);
      onOpenChange(false);
      setForm({ customer_id: '', customer_name: '', amount: '', payment_mode: 'cash', deposit_to: '', clinic_id: '', clinic_name: '', payment_date: new Date().toISOString().split('T')[0], reference: '', notes: '' });
      setCustQuery('');
      if (onSaved) onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record advance'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="record-advance-dialog">
        <DialogHeader><DialogTitle>Record Customer Advance</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <label className="text-xs text-gray-500 block mb-1">Customer *</label>
            <Input value={custQuery} disabled={!!prefillCustomer}
              onChange={e => { setCustQuery(e.target.value); setShowCustDrop(true); setForm(f => ({ ...f, customer_id: '', customer_name: e.target.value })); }}
              onFocus={() => setShowCustDrop(true)}
              placeholder="Search customer by name/phone..." data-testid="advance-customer-search" />
            {showCustDrop && custResults.length > 0 && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-52 overflow-y-auto">
                {custResults.map(c => {
                  const name = c.display_name || c.contact_name || c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim();
                  return (
                    <button key={c.id || c.zoho_id} type="button" onMouseDown={e => { e.preventDefault(); selectCustomer(c); }}
                      className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 border-b border-gray-100 last:border-0" data-testid={`advance-cust-opt-${c.id || c.zoho_id}`}>
                      <div className="font-medium text-[#0B1B3D]">{name}</div>
                      {c.phone && <div className="text-[10px] text-gray-400">{c.phone}</div>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Amount (₹) *</label>
              <Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" data-testid="advance-amount" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Date</label>
              <Input type="date" value={form.payment_date} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} data-testid="advance-date" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Payment Mode</label>
              <Select value={form.payment_mode} onValueChange={v => setForm(f => ({ ...f, payment_mode: v }))}>
                <SelectTrigger data-testid="advance-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_MODES.map(m => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Deposit To (bank)</label>
              <SearchableSelect value={form.deposit_to || 'none'} onChange={v => setForm(f => ({ ...f, deposit_to: v === 'none' ? '' : v }))} placeholder="Optional" testid="advance-deposit" buttonClassName="w-full"
                  options={[{ value: 'none', label: '— None —' }, ...banks.map(b => ({ value: b.name || b.account_name, label: b.name || b.account_name }))]} />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500 block mb-1">Clinic (for cash ledger)</label>
              <SearchableSelect value={form.clinic_id || 'none'} onChange={v => setForm(f => ({ ...f, clinic_id: v === 'none' ? '' : v }))} placeholder="Optional" testid="advance-clinic" buttonClassName="w-full"
                  options={[{ value: 'none', label: '— None —' }, ...clinics.map(c => ({ value: c.id, label: c.name }))]} />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500 block mb-1">Reference / Notes</label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. token advance for hearing aid" data-testid="advance-notes" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="advance-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0B1B3D] text-white" data-testid="advance-submit">
            {saving ? 'Saving...' : 'Record Advance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
